import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useRef, useEffect, useCallback } from 'react';
import { Button, TabBar, Badge, Alert } from '@thegpvc/ui';
import { X } from 'lucide-react';
import { api } from '../api/client';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { SectionCard } from '../components/SectionCard';
import { SessionTable } from '../components/SessionTable';
import { FilterDropdown } from '../components/FilterDropdown';
import { text, cx } from '../styles/theme';
import { pluralize } from '../utils/format';
import { errorMessage } from '../utils/errorMessage';
import { SOURCE_FILTERS, sourceLabel } from '../utils/sourceLabels';
import type { TimePeriod } from '../types/api';

const PAGE_SIZE = 25;

const periods = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
  { id: 'all', label: 'All Time' },
];

export function Sessions() {
  // The URL is the single source of truth for filters (see useUrlFilters). This
  // keeps filtered views bookmarkable/shareable, makes browser back/forward work,
  // and ensures in-table project/hostname links (which navigate to
  // /sessions?project=…) actually apply.
  const { searchParams, setParam, clearAllFilters } = useUrlFilters();
  const projectFilter = searchParams.get('project') || '';
  const hostnameFilter = searchParams.get('hostname') || '';
  const anomalyFilter = searchParams.get('anomaly') === 'true';
  const frictionFilter = searchParams.get('friction') === 'true';
  const sourceFilter = searchParams.get('source') || '';
  const period = (searchParams.get('period') as TimePeriod) || 'all';
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Fetch filter options for dropdowns
  const { data: filterData } = useQuery({
    queryKey: ['filterOptions'],
    queryFn: () => api.getFilterOptions(),
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['sessions', projectFilter, hostnameFilter, anomalyFilter, frictionFilter, sourceFilter, period],
    queryFn: ({ pageParam = 0 }) =>
      api.listSessions({
        project: projectFilter || undefined,
        hostname: hostnameFilter || undefined,
        period,
        limit: PAGE_SIZE,
        offset: pageParam,
        anomalyOnly: anomalyFilter || undefined,
        frictionOnly: frictionFilter || undefined,
        source: sourceFilter || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.sessions.length, 0);
      const total = parseInt(lastPage.total || '0', 10);
      if (totalFetched >= total) return undefined;
      return totalFetched;
    },
    placeholderData: keepPreviousData,
  });

  // Flatten all pages into a single sessions array
  const sessions = data?.pages.flatMap((page) => page.sessions) ?? [];

  // Total sessions matching the active filters, reported by the API on the
  // first page. Surfaced next to the title so the match count is visible
  // immediately, rather than only after infinite scroll loads every page.
  const totalCount = data ? parseInt(data.pages[0]?.total || '0', 10) : undefined;

  const hasActiveFilters =
    !!projectFilter || !!hostnameFilter || anomalyFilter || frictionFilter || !!sourceFilter || period !== 'all';

  // IntersectionObserver for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (error) {
    return (
      <PageContainer>
        <title>Dixie :: Sessions</title>
        <PageHeader title="Sessions" />
        <Alert variant="error">Failed to load sessions: {errorMessage(error)}</Alert>
      </PageContainer>
    );
  }

  const hostnames = filterData?.hostnames ?? [];
  const projects = filterData?.projects ?? [];

  return (
    <PageContainer>
      <title>Dixie :: Sessions</title>
      <PageHeader title="Sessions" count={totalCount}>
        {anomalyFilter && (
          <button
            onClick={() => setParam('anomaly', '')}
            className="hover:opacity-80"
            aria-label="Clear anomalous-only filter"
          >
            <Badge variant="warning">
              Anomalous only
              <X className="w-3.5 h-3.5 ml-1" />
            </Badge>
          </button>
        )}
        {frictionFilter && (
          <button
            onClick={() => setParam('friction', '')}
            className="hover:opacity-80"
            aria-label="Clear friction-only filter"
          >
            <Badge variant="error">
              Friction only
              <X className="w-3.5 h-3.5 ml-1" />
            </Badge>
          </button>
        )}
        <FilterDropdown
          triggerLabel={projectFilter || 'All Projects'}
          allLabel="All Projects"
          options={projects.map((project) => ({ value: project, label: project }))}
          value={projectFilter}
          onSelect={(value) => setParam('project', value)}
        />
        <FilterDropdown
          triggerLabel={hostnameFilter || 'All Devices'}
          allLabel="All Devices"
          options={hostnames.map((hostname) => ({ value: hostname, label: hostname }))}
          value={hostnameFilter}
          onSelect={(value) => setParam('hostname', value)}
        />
        <FilterDropdown
          triggerLabel={sourceFilter ? sourceLabel(sourceFilter) : 'All Sources'}
          allLabel="All Sources"
          options={SOURCE_FILTERS}
          value={sourceFilter}
          onSelect={(value) => setParam('source', value)}
        />
        <TabBar
          variant="pill"
          items={periods}
          activeId={period}
          onChange={(id: string) => setParam('period', id, 'all')}
        />
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            Clear filters
          </Button>
        )}
      </PageHeader>

      {/* Sessions table wrapped as a flush card section. */}
      <SectionCard title="Results" flush className="overflow-hidden">
        {isLoading ? (
          // Skeleton rows on cold load (no cached data), matching the table's
          // row height so the layout doesn't jump when results arrive — the
          // same pulsing-placeholder pattern the Reports/Projects/Stats lists
          // use, rather than a bare centered "Loading…". role="status" + an
          // sr-only label so assistive tech announces the loading state.
          <div role="status" aria-label="Loading sessions" aria-busy="true" className="space-y-3 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                aria-hidden="true"
                className="h-10 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
              />
            ))}
            <span className="sr-only">Loading sessions…</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className={cx('text-sm', text.secondary)}>
              {hasActiveFilters
                ? 'No sessions match the current filters.'
                : 'No sessions found'}
            </p>
            {hasActiveFilters && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-3"
                onClick={clearAllFilters}
              >
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <SessionTable
              sessions={sessions}
              showAutonomy
              showTimeBreakdown
              showHostname
              showTokens
            />
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} className="h-1" />
            {isFetchingNextPage && (
              <div className={cx('px-4 py-4 text-center', text.secondary)}>
                Loading more sessions…
              </div>
            )}
            {!hasNextPage && sessions.length > 0 && (
              <div className={cx('px-4 py-3 text-center text-xs', text.muted)}>
                All {sessions.length} {pluralize(sessions.length, 'session')} loaded
              </div>
            )}
          </>
        )}
      </SectionCard>
    </PageContainer>
  );
}
